import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

export default function PaymentSetup() {
  const { profile, coachProfile, isPartner, isSeniorCoach, refreshProfile } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile?.id) checkStatus();
  }, [profile]);

  // Check if we're returning from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'complete' || params.get('stripe') === 'refresh') {
      checkStatus();
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/connect/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/connect/onboard`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start Stripe setup');

      // Redirect to Stripe's hosted onboarding
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setConnecting(false);
    }
  };

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 32 }}>Checking payment status...</div>;

  const isConnected = status?.onboardingComplete;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>Payment Setup</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Connect your bank account to receive payments from clients
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Status card */}
      <div style={{
        background: isConnected ? 'rgba(76,175,130,0.08)' : 'rgba(240,165,0,0.08)',
        border: `1px solid ${isConnected ? 'rgba(76,175,130,0.25)' : 'rgba(240,165,0,0.25)'}`,
        borderRadius: 12, padding: '20px 24px', marginBottom: 28,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: isConnected ? 'rgba(76,175,130,0.2)' : 'rgba(240,165,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20
          }}>
            {isConnected ? '✓' : '⚡'}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--text-primary)' }}>
              {isConnected ? 'Stripe Account Connected' : 'Payment Setup Required'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {isConnected
                ? `Charges enabled · Payouts enabled · You can now accept payments`
                : 'Complete Stripe onboarding to accept payments and receive payouts'}
            </div>
          </div>
        </div>
        {!isConnected && (
          <button className="btn btn-primary" onClick={handleConnect} disabled={connecting}>
            {connecting ? 'Redirecting to Stripe...' : 'Set Up Payments →'}
          </button>
        )}
        {isConnected && (
          <button className="btn btn-secondary" onClick={handleConnect} disabled={connecting}>
            {connecting ? 'Loading...' : 'Manage in Stripe'}
          </button>
        )}
      </div>

      {/* How it works */}
      {!isConnected && (
        <div className="card">
          <h3 style={{ fontSize: 18, marginBottom: 20 }}>How It Works</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { step: '1', title: 'Connect with Stripe', desc: 'Click Set Up Payments and complete Stripe\'s secure onboarding. Takes about 5 minutes.' },
              { step: '2', title: 'Create your products', desc: 'Add your coaching packages and services. Each gets a shareable payment link.' },
              { step: '3', title: 'Share with clients', desc: 'Send your payment links to clients. They pay securely through Stripe.' },
              { step: '4', title: 'Get paid automatically', desc: `Revenue splits automatically — you keep your share, HMA receives theirs. Payouts go directly to your bank.` }
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(201,169,110,0.15)', border: '1px solid rgba(201,169,110,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: 'var(--accent)'
                }}>{item.step}</div>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24 }}>
            <button className="btn btn-primary" onClick={handleConnect} disabled={connecting}
              style={{ justifyContent: 'center' }}>
              {connecting ? 'Redirecting to Stripe...' : 'Get Started with Stripe →'}
            </button>
          </div>
        </div>
      )}

      {/* Connected — show details */}
      {isConnected && (
        <div className="card">
          <h3 style={{ fontSize: 18, marginBottom: 20 }}>Account Details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Charges', value: status?.chargesEnabled ? '✓ Enabled' : '✗ Not enabled', color: status?.chargesEnabled ? 'var(--success)' : 'var(--danger)' },
              { label: 'Payouts', value: status?.payoutsEnabled ? '✓ Enabled' : '✗ Not enabled', color: status?.payoutsEnabled ? 'var(--success)' : 'var(--danger)' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '12px 0', borderBottom: '1px solid var(--border)'
              }}>
                <span style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
                <span style={{ fontWeight: 500, color: item.color }}>{item.value}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 16 }}>
            To update your bank account or payout settings, click "Manage in Stripe" above.
          </p>
        </div>
      )}
    </div>
  );
}
